import { z } from 'zod';
import { hexColorSchema, hillPositionSchema } from './primitives.js';

/**
 * A dot on a hill chart - maps 1-to-1 to a scope of the upstream project
 * (a milestone, whatever the provider happens to call it). Created implicitly
 * when a hill chart is imported, mutated by the user through the dot PATCH
 * route (position, color, completion), and realigned with the upstream
 * provider by a resync.
 *
 * Naming choice: `externalScopeName` / `externalScopeUrl` (rather than
 * bare `name` / `url`) keeps the origin explicit on the wire. A dot's name
 * is not editable by humans - it mirrors whatever the upstream provider
 * currently holds.
 *
 * `lastMovedByUserId` and `lastMovedAt` are both nullable because a freshly
 * imported dot has not been moved yet. The UI renders "untouched" in that
 * state; the resync path never overwrites move timestamps.
 */
export const dotSchema = z.object({
  id: z.string().uuid(),
  hillChartId: z.string().uuid(),
  externalScopeId: z.string(),
  externalScopeName: z.string(),
  externalScopeUrl: z.string().url().nullable(),
  x: hillPositionSchema,
  color: hexColorSchema,
  completed: z.boolean(),
  lastMovedByUserId: z.string().uuid().nullable(),
  lastMovedAt: z.string().datetime().nullable(),
  /**
   * Stable, monotonic per-hill dot number rendered as "#1, #2, …" in the
   * chart and the legend. Assigned at insert time and never mutated — the
   * key invariant powering the "this milestone is always #3" guarantee
   * across the live view and every historical snapshot of the same hill.
   * Always >= 1; gaps are allowed (a deleted dot keeps its number to
   * itself forever).
   */
  displayOrder: z.number().int().positive(),
});
export type Dot = z.infer<typeof dotSchema>;
